import React, { useEffect, useMemo, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Package, ScanLine, CheckCircle2, XCircle, RotateCcw, Tag, Calendar, Clock, MapPin, Repeat, Search, RefreshCw, LogOut, ChevronRight, AlertTriangle, QrCode, Boxes, Store, Wallet, Users, LayoutDashboard, Star } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { QrScanner } from '../components/QrScanner';
import { useStore, describeRecurrence, isRecurring, nextOccurrence, normalizeRecurrence, normalizeVerificationMethod, redeemedCount, findEventByCode, normalizeCode, partnerLocationLabel, VERIFICATION_METHODS } from '../lib/store';
import type { Event, PartnerCode } from '../lib/store';
import { onDataChanged } from '../lib/liveSync';
import { fetchAdminAuth, fetchAdminSnapshot, patchCatch, DEFAULT_ADMIN_AUTH } from '../lib/supabase';
import type { AdminCatch, AdminUser } from '../lib/supabase';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/bcWTzmedNUNKBwzbD46o8N/2.png';
type Verdict = {
  kind: 'valid' | 'redeemed' | 'invalid' | 'drop';
  title: string;
  detail?: string;
};
/**
 * DISTRIBUTION PARTNER PORTAL (dark, staff/partner tool).
 *
 * Reachable at /partner. Password-gated with the partner password stored in
 * Supabase (app_config "adminAuth"). Partners can:
 *   • Browse every live drop with brand, products, schedule (incl. recurrence)
 *     and a REAL-TIME redeemed/remaining counter (polled from Supabase).
 *   • Scan a customer's redemption QR (or a drop QR) with the camera to verify
 *     and redeem on the spot — the same verification the admin Verify tab uses.
 *
 * Supabase is the single source of truth: drops come from the shared store
 * (hydrated app-wide), catches come straight from the admin snapshot.
 */
export function PartnerPortal() {
  const {
    events,
    partnerCodes,
    hydrateDrops,
    hydratePartnerCodes
  } = useStore();
  // The signed-in partner (their store identity), or null when locked.
  const [partner, setPartner] = useState<PartnerCode | null>(null);
  const [codeInput, setCodeInput] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [partnerPassword, setPartnerPassword] = useState(DEFAULT_ADMIN_AUTH.partnerPassword);
  const authed = partner !== null;
  const [tab, setTab] = useState<'overview' | 'drops' | 'verify'>('overview');
  // Live catch + user data (for verification + redeemed counts cross-device).
  const [catches, setCatches] = useState<AdminCatch[]>([]);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [loading, setLoading] = useState(false);
  // Verify (camera) state.
  const [showScanner, setShowScanner] = useState(false);
  const [verdict, setVerdict] = useState<Verdict | null>(null);
  // Selected drop detail.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  useEffect(() => {
    let active = true;
    fetchAdminAuth().then(cfg => {
      if (active) setPartnerPassword(cfg.partnerPassword);
    });
    // Make sure the partner-code directory is fresh so sign-in resolves.
    hydratePartnerCodes();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const loadData = async () => {
    setLoading(true);
    const [snap] = await Promise.all([fetchAdminSnapshot(), hydrateDrops()]);
    setCatches(snap.catches);
    setUsers(snap.users);
    setLastRefreshed(new Date());
    setLoading(false);
  };
  // Poll live data every 10s while the partner is signed in, and refresh
  // instantly when data changes on any other surface (admin/user/brand).
  useEffect(() => {
    if (!authed) return;
    loadData();
    const interval = setInterval(loadData, 10000);
    const unsub = onDataChanged(() => loadData());
    return () => {
      clearInterval(interval);
      unsub();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);
  const nameForPhone = (phone: string) => users.find(u => u.phone === phone)?.name || 'User';
  // Pull a usable code out of a scanned QR (deep link or raw code).
  const extractCode = (raw: string): string => {
    const text = (raw || '').trim();
    try {
      const url = new URL(text);
      const param = url.searchParams.get('drop') || url.searchParams.get('code') || url.searchParams.get('userCode');
      if (param) return param.trim();
    } catch {
      /* not a URL */
    }
    return text;
  };
  const handleScannedCode = async (raw: string) => {
    setShowScanner(false);
    const code = extractCode(raw);
    const upper = code.toUpperCase();
    if (!code) {
      setVerdict({
        kind: 'invalid',
        title: 'Nothing detected'
      });
      return;
    }
    // 1) A customer's redemption (user) code.
    const match = catches.find(c => c.userCode.toUpperCase() === upper);
    if (match) {
      const who = nameForPhone(match.userId);
      const product = match.eventBrand || match.eventTitle || 'Catch';
      const already = match.status === 'redeemed' || match.status === 'reviewed';
      if (already) {
        setVerdict({
          kind: 'redeemed',
          title: 'Already redeemed',
          detail: `${who} • ${product} • status: ${match.status}`
        });
        return;
      }
      await patchCatch(match.id, {
        status: 'redeemed'
      });
      setCatches(prev => prev.map(c => c.id === match.id ? {
        ...c,
        status: 'redeemed'
      } : c));
      setVerdict({
        kind: 'valid',
        title: 'Valid — catch unlocked',
        detail: `${who} • ${product}`
      });
      loadData();
      return;
    }
    // 2) A recognised drop QR (not a redemption code).
    const drop = findEventByCode(events, code);
    if (drop) {
      setVerdict({
        kind: 'drop',
        title: 'Drop QR recognised',
        detail: `${drop.brand} • ${drop.campaign || drop.title} (${normalizeCode(drop.code)})`
      });
      return;
    }
    setVerdict({
      kind: 'invalid',
      title: 'Invalid code',
      detail: `"${code}" doesn't match any redemption code or drop.`
    });
  };
  // Live redeemed count per drop: combine the drop's own counter (total-left)
  // with the count of redeemed/reviewed catches synced for that drop. The
  // higher of the two is the truth (covers cross-device redemptions).
  const redeemedForDrop = (drop: Event) => {
    const fromCounter = redeemedCount(drop);
    const fromCatches = catches.filter(c => c.eventTitle === drop.title && (c.status === 'redeemed' || c.status === 'reviewed')).length;
    return Math.max(fromCounter, fromCatches);
  };
  // ---- PARTNER-SCOPED ATTRIBUTION (the payout basis) ----
  // A redemption "counts" for a partner when the customer joined via that
  // partner's code. Redemptions are what define the partner's payment, so this
  // is the number the portal foregrounds.
  const partnerCodeUpper = (partner?.code || '').toUpperCase();
  const partnerUserPhones = useMemo(() => {
    const set = new Set<string>();
    users.forEach(u => {
      if ((u.partnerCode || '').toUpperCase() === partnerCodeUpper) set.add(u.phone);
    });
    return set;
  }, [users, partnerCodeUpper]);
  const partnerCatches = useMemo(() => catches.filter(c => partnerUserPhones.has(c.userId)), [catches, partnerUserPhones]);
  const partnerRedeemed = partnerCatches.filter(c => c.status === 'redeemed' || c.status === 'reviewed');
  const partnerReviewed = partnerCatches.filter(c => c.status === 'reviewed');
  // Drops physically hosted at this partner's location (matched by address),
  // so a store sees the drops they actually hand out. Falls back to all drops
  // when none are tagged to this address yet.
  const partnerDrops = useMemo(() => {
    if (!partner) return events;
    const street = (partner.address || '').toLowerCase().trim();
    if (!street) return events;
    const matched = events.filter(e => (e.address || '').toLowerCase().includes(street));
    return matched.length > 0 ? matched : events;
  }, [events, partner]);
  const filteredEvents = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = partnerDrops;
    if (!q) return base;
    return base.filter(e => [e.title, e.brand, e.campaign, e.code, e.address].filter(Boolean).some(v => String(v).toLowerCase().includes(q)));
  }, [partnerDrops, query]);
  const selected = events.find(e => e.id === selectedId) || null;
  // ---------- LOGIN GATE ----------
  // A partner signs in with THEIR partner code (their store identity) + the
  // shared partner password. The code resolves to the store's name + location.
  if (!authed) {
    return <div className="min-h-screen bg-gray-950 flex flex-col items-center justify-center p-4 text-white" data-id="element-2258" data-mp="3v9kstq3wxd8r">
        <div className="w-64 sm:w-72 max-w-full mb-8" data-id="element-2259" data-mp="901fhxgv1hpy">
          <img src={LOGO_URL} alt="GONE FISHING" className="w-full h-auto object-contain" data-id="element-2260" data-mp="8z8j9qfj2am2" />
        </div>
        <div className="bg-gray-900 border border-gray-800 p-8 rounded-2xl w-full max-w-sm" data-id="element-2261" data-mp="i0pxt4dae48n">
          <div className="flex items-center justify-center mb-5" data-id="element-2262" data-mp="mzszceu25sbv">
            <span className="inline-flex items-center gap-2 bg-brand-blue/15 text-brand-blue text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full" data-id="element-2263" data-mp="mxyf72qm248u">
              <Store className="w-4 h-4" data-id="element-2264" data-mp="wpvscf16j6a7" /> Partner Portal
            </span>
          </div>
          <p className="text-gray-400 text-sm text-center mb-5" data-id="element-2265" data-mp="10rpms5ks7e1x">
            Sign in with your store's partner code and password to see your
            drops and redemptions.
          </p>
          <form onSubmit={e => {
          e.preventDefault();
          const code = codeInput.trim().toUpperCase();
          const match = partnerCodes.find(p => p.code.toUpperCase() === code);
          if (!match) {
            setLoginError("We don't recognise that partner code.");
            return;
          }
          if (!match.active) {
            setLoginError('This partner code is inactive.');
            return;
          }
          if (password !== partnerPassword) {
            setLoginError('Wrong partner password.');
            return;
          }
          setLoginError('');
          setPartner(match);
          setTab('overview');
        }} data-id="element-2266" data-mp="1q61t36vjw2pm">
            <label className="text-xs text-gray-500 uppercase font-bold mb-1 block" data-id="element-2267" data-mp="ogpn5xqwkeq7">
              Partner code
            </label>
            <Input placeholder="e.g. SEVEN-DTG56" value={codeInput} onChange={e => setCodeInput(e.target.value.toUpperCase())} className="bg-gray-800 border-gray-700 text-white mb-3 text-center font-mono uppercase" autoFocus data-id="element-2268" data-mp="tg2a0ihrxlss" />
            <label className="text-xs text-gray-500 uppercase font-bold mb-1 block" data-id="element-2269" data-mp="2xxqqjk5wu8q5">
              Partner password
            </label>
            <Input type="password" placeholder="Partner password" value={password} onChange={e => setPassword(e.target.value)} className="bg-gray-800 border-gray-700 text-white mb-4 text-center" data-id="element-2270" data-mp="3poel2dd2wfuu" />
            {loginError && <p className="text-red-400 text-xs mb-3 text-center" data-id="element-2271" data-mp="3bovomhtmqsyd">
                {loginError}
              </p>}
            <Button type="submit" className="w-full" data-id="element-2272" data-mp="259kz6ekcplhc">
              Enter Portal
            </Button>
          </form>
        </div>
      </div>;
  }
  // ---------- PORTAL ----------
  return <div className="min-h-screen w-full bg-gray-950 text-white" data-id="element-2273" data-mp="21royr4a8i63f">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-gray-950/90 backdrop-blur border-b border-gray-800" data-id="element-2274" data-mp="26pyvhjblxer5">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-3" data-id="element-2275" data-mp="1sqqtkio27sj9">
          <div className="flex items-center gap-3 min-w-0" data-id="element-2276" data-mp="1xoisvxr1agxh">
            <img src={LOGO_URL} alt="Gone Fishing" className="h-8 w-auto shrink-0" data-id="element-2277" data-mp="1xqvb8c0petb2" />
            <div className="min-w-0" data-id="element-2278" data-mp="1nr4fk70eq89e">
              <div className="flex items-center gap-2" data-id="element-2279" data-mp="1ns5redp8yys6">
                <Store className="w-4 h-4 text-brand-blue shrink-0" data-id="element-2280" data-mp="19zzkli7zi45e" />
                <span className="font-bold text-white truncate" data-id="element-2281" data-mp="19xmyqtz3v9n2">
                  {partner?.name}
                </span>
              </div>
              {partnerLocationLabel(partner || {}) && <p className="text-[11px] text-gray-400 truncate" data-id="element-2282" data-mp="103mlu5xl103s">
                  {partnerLocationLabel(partner || {})}
                </p>}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0" data-id="element-2283" data-mp="lgeexhbb3gxm">
            <button onClick={loadData} className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white px-2.5 py-1.5 rounded-lg hover:bg-gray-800 transition-colors" aria-label="Refresh data" data-id="element-2284" data-mp="ggs0j4jqrun8">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} data-id="element-2285" data-mp="14d5vvk3dljyi" />
              <span className="hidden sm:inline" data-id="element-2286" data-mp="zevzj65x0dbb">Refresh</span>
            </button>
            <button onClick={() => {
            setPartner(null);
            setCodeInput('');
            setPassword('');
          }} className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white px-2.5 py-1.5 rounded-lg hover:bg-gray-800 transition-colors" data-id="element-2287" data-mp="1e2z5wgnvdr0n">
              <LogOut className="w-4 h-4" data-id="element-2288" data-mp="1j3d1v0c75aib" />
              <span className="hidden sm:inline" data-id="element-2289" data-mp="1j0iilc4x5vqh">Sign out</span>
            </button>
          </div>
        </div>
        {/* Tabs */}
        <div className="max-w-5xl mx-auto px-4" data-id="element-2290" data-mp="3hjlwnqkmoivt">
          <div className="flex gap-1" data-id="element-2291" data-mp="3hiaws1uazv9z">
            {([{
            id: 'overview',
            label: 'Overview',
            icon: LayoutDashboard
          }, {
            id: 'drops',
            label: 'Drops',
            icon: Boxes
          }, {
            id: 'verify',
            label: 'Verify',
            icon: ScanLine
          }] as const).map(t => {
            const Icon = t.icon;
            const active = tab === t.id;
            return <button key={t.id} onClick={() => setTab(t.id)} className={`relative flex items-center gap-1.5 px-4 py-3 text-sm font-bold transition-colors ${active ? 'text-white' : 'text-gray-500 hover:text-gray-300'}`} data-id="element-2292" data-mp="iuohghc0zixc">
                  <Icon className="w-4 h-4" data-id="element-2293" data-mp="23cfqvgi0xqw3" />
                  {t.label}
                  {active && <motion.span layoutId="partner-tab" className="absolute left-0 right-0 -bottom-px h-0.5 bg-brand-blue rounded-full" data-id="element-2294" data-mp="1ykxqatjwxsbn" />}
                </button>;
          })}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6" data-id="element-2295" data-mp="1jn5eabdkrm27">
        {tab === 'overview' && <div className="space-y-6" data-id="element-2296" data-mp="1fltmq0b54qrd">
            {/* Store identity card */}
            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5" data-id="element-2297" data-mp="1ans23ay2gjds">
              <div className="flex items-start gap-4" data-id="element-2298" data-mp="1apv46tchu2yv">
                <div className="w-12 h-12 rounded-xl bg-brand-blue/15 flex items-center justify-center shrink-0" data-id="element-2299" data-mp="39zbdrxar85r5">
                  <Store className="w-6 h-6 text-brand-blue" data-id="element-2300" data-mp="3522vzxtiq3qf" />
                </div>
                <div className="min-w-0" data-id="element-2301" data-mp="3o5d11wi018xr">
                  <h1 className="text-xl font-bold truncate" data-id="element-2302" data-mp="3o4bqru8t4qh3">
                    {partner?.name}
                  </h1>
                  {partnerLocationLabel(partner || {}) ? <p className="text-sm text-gray-400 flex items-center gap-1.5 mt-0.5" data-id="element-2303" data-mp="3f3dhoxjihsrn">
                      <MapPin className="w-4 h-4 text-gray-500 shrink-0" data-id="element-2304" data-mp="3k4t1km9ydt8g" />
                      {partnerLocationLabel(partner || {})}
                    </p> : <p className="text-sm text-gray-500 mt-0.5" data-id="element-2305" data-mp="fp1skx81g5el">
                      No location set for this partner.
                    </p>}
                  <span className="inline-block mt-2 font-mono text-[11px] bg-brand-yellow/15 text-brand-yellow px-2 py-0.5 rounded" data-id="element-2306" data-mp="6p5714gy3mnr">
                    {partner?.code}
                  </span>
                </div>
              </div>
            </div>

            {/* Payment-defining redemption stats */}
            <div data-id="element-2307" data-mp="2jvlbpm1bj5qc">
              <div className="flex items-center gap-2 mb-3" data-id="element-2308" data-mp="2jv1sm1zxmbaq">
                <Wallet className="w-4 h-4 text-brand-blue" data-id="element-2309" data-mp="2exjs8zzzd4ec" />
                <h2 className="text-sm font-bold uppercase tracking-wider text-gray-300" data-id="element-2310" data-mp="2ex0cnpxsys3h">
                  Redemptions (your payout basis)
                </h2>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-2311" data-mp="2nz9iw3m1fzb2">
                <StatCard icon={CheckCircle2} label="Redeemed" value={partnerRedeemed.length} accent="text-green-400" hint="Counts toward payment" data-id="element-2312" data-mp="207vntpwi5pme" />
                <StatCard icon={Users} label="Customers" value={partnerUserPhones.size} accent="text-brand-blue" hint="Joined via your code" data-id="element-2313" data-mp="2a09wl2lzzwi2" />
                <StatCard icon={Star} label="Reviewed" value={partnerReviewed.length} accent="text-brand-yellow" hint="Redeemed + reviewed" data-id="element-2314" data-mp="1cjkur8x1su8o" />
                <StatCard icon={Boxes} label="Total catches" value={partnerCatches.length} accent="text-gray-200" hint="Reserved + redeemed" data-id="element-2315" data-mp="w0nmmu7zpmgv" />
              </div>
              <p className="text-xs text-gray-500 mt-3 leading-relaxed" data-id="element-2316" data-mp="1av26sy3aj1x4">
                These figures are attributed to customers who joined with your
                partner code{' '}
                <span className="font-mono text-gray-400" data-id="element-2317" data-mp="1jp6tr6fazt7d">{partner?.code}</span>
                . Redemptions are what define your payment. Updated{' '}
                {lastRefreshed ? lastRefreshed.toLocaleTimeString() : 'just now'}
                .
              </p>
            </div>

            {/* Recent redemptions list */}
            <div data-id="element-2318" data-mp="3j372161a6ci0">
              <h2 className="text-sm font-bold uppercase tracking-wider text-gray-300 mb-3" data-id="element-2319" data-mp="3j3ql4q2o36xm">
                Recent redemptions
              </h2>
              {partnerRedeemed.length === 0 ? <div className="bg-gray-900 border border-dashed border-gray-800 rounded-2xl p-8 text-center" data-id="element-2320" data-mp="394rcv8ltddb5">
                  <Wallet className="w-8 h-8 text-gray-600 mx-auto mb-3" data-id="element-2321" data-mp="3479ay267rycn" />
                  <p className="text-gray-400 text-sm" data-id="element-2322" data-mp="5oaqlzkoob6p">
                    No redemptions yet. They appear here the moment a customer
                    who joined with your code redeems a catch.
                  </p>
                </div> : <div className="bg-gray-900 border border-gray-800 rounded-2xl divide-y divide-gray-800 overflow-hidden" data-id="element-2323" data-mp="3s2bx22dmvnpf">
                  {partnerRedeemed.slice().sort((a, b) => b.timestamp - a.timestamp).slice(0, 12).map(c => <div key={c.id} className="flex items-center justify-between gap-3 px-4 py-3" data-id="element-2324" data-mp="2a95xkg8mcwzv">
                        <div className="min-w-0" data-id="element-2325" data-mp="258rp4x33vl1b">
                          <p className="font-medium text-white truncate" data-id="element-2326" data-mp="217zhg89vto75">
                            {nameForPhone(c.userId)}
                          </p>
                          <p className="text-xs text-gray-500 truncate" data-id="element-2327" data-mp="1wbhffg5xs3yi">
                            {c.eventBrand || c.eventTitle || 'Catch'}
                          </p>
                        </div>
                        <div className="text-right shrink-0" data-id="element-2328" data-mp="2p06ezarcg185">
                          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${c.status === 'reviewed' ? 'bg-green-500/15 text-green-400' : 'bg-amber-500/15 text-amber-400'}`} data-id="element-2329" data-mp="2k6296uttdz8n">
                            {c.status}
                          </span>
                          <p className="text-[11px] text-gray-500 mt-1" data-id="element-2330" data-mp="37v3be0c20e5h">
                            {new Date(c.timestamp).toLocaleDateString()}
                          </p>
                        </div>
                      </div>)}
                </div>}
            </div>
          </div>}

        {tab === 'drops' && <div className="space-y-5" data-id="element-2331" data-mp="3t8n0xmihqyoz">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-id="element-2332" data-mp="3t5is2bu2zerz">
              <div data-id="element-2333" data-mp="1zpzcap0ciox">
                <h1 className="text-xl font-bold" data-id="element-2334" data-mp="70dqqxh5f1jo">Your Drops</h1>
                <p className="text-sm text-gray-400" data-id="element-2335" data-mp="6yki5cmz6791">
                  {partnerDrops.length} drop
                  {partnerDrops.length === 1 ? '' : 's'} ·{' '}
                  {lastRefreshed ? `updated ${lastRefreshed.toLocaleTimeString()}` : 'syncing…'}
                </p>
              </div>
              <div className="relative sm:w-72" data-id="element-2336" data-mp="pqlnmxu98vrf">
                <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-2337" data-mp="prenrfypkn0l" />
                <Input placeholder="Search drops, brands…" value={query} onChange={e => setQuery(e.target.value)} className="bg-gray-900 border-gray-800 text-white pl-9" data-id="element-2338" data-mp="2lujbolmm0l5v" />
              </div>
            </div>

            {filteredEvents.length === 0 ? <div className="bg-gray-900 border border-dashed border-gray-800 rounded-2xl p-10 text-center" data-id="element-2339" data-mp="35hr3pm8odp2f">
                <Package className="w-8 h-8 text-gray-600 mx-auto mb-3" data-id="element-2340" data-mp="30jyzpturachv" />
                <p className="text-gray-400 text-sm" data-id="element-2341" data-mp="3eiysndpaxdju">
                  {events.length === 0 ? 'No drops are live yet. They appear here automatically when admins create them.' : 'No drops match your search.'}
                </p>
              </div> : <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2342" data-mp="1f6302vu8xhkv">
                {filteredEvents.map(e => {
            const redeemed = redeemedForDrop(e);
            const remaining = Math.max(0, e.totalBoxes - redeemed);
            const pct = e.totalBoxes > 0 ? Math.min(100, Math.round(redeemed / e.totalBoxes * 100)) : 0;
            const rec = normalizeRecurrence(e.recurrence);
            const next = nextOccurrence(e);
            return <button key={e.id} onClick={() => setSelectedId(e.id)} className="text-left bg-gray-900 border border-gray-800 hover:border-brand-blue/50 rounded-2xl p-5 transition-colors flex flex-col" data-id="element-2343" data-mp="3d2l08k1624fb">
                      <div className="flex items-start justify-between gap-3 mb-3" data-id="element-2344" data-mp="xbyovn0sogsh">
                        <div className="flex items-center gap-3 min-w-0" data-id="element-2345" data-mp="11b7hupo0gamz">
                          {e.brandLogo ? <img src={e.brandLogo} alt="" className="w-10 h-10 rounded-lg object-contain bg-white/5 p-1 shrink-0" data-id="element-2346" data-mp="cl2w1xenuxyu" /> : <div className="w-10 h-10 rounded-lg bg-gray-800 flex items-center justify-center shrink-0" data-id="element-2347" data-mp="3tsol2hegh8hu">
                              <Tag className="w-5 h-5 text-gray-500" data-id="element-2348" data-mp="3pto659z3mq04" />
                            </div>}
                          <div className="min-w-0" data-id="element-2349" data-mp="3g2bas8e3dhpc">
                            <h3 className="font-bold text-white truncate" data-id="element-2350" data-mp="3g339vt6wdxkd">
                              {e.title}
                            </h3>
                            <p className="text-xs text-blue-400 truncate" data-id="element-2351" data-mp="36447gscq6y5t">
                              {e.brand}
                            </p>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-600 shrink-0 mt-1" data-id="element-2352" data-mp="1hkroagphos13" />
                      </div>

                      <div className="flex flex-wrap gap-1.5 mb-3" data-id="element-2353" data-mp="1lljvl4eswmuq">
                        <span className="font-mono text-[11px] bg-brand-yellow/15 text-brand-yellow px-2 py-0.5 rounded" data-id="element-2354" data-mp="1lnxph9xqvt0p">
                          {e.code}
                        </span>
                        {isRecurring(rec) && <span className="inline-flex items-center gap-1 text-[11px] bg-brand-blue/15 text-brand-blue px-2 py-0.5 rounded" data-id="element-2355" data-mp="xxa7twy11p0y">
                            <Repeat className="w-3 h-3" data-id="element-2356" data-mp="12tis1or308ez" />
                            {describeRecurrence(rec)}
                          </span>}
                      </div>

                      {/* Real-time redeemed counter */}
                      <div className="mt-auto" data-id="element-2357" data-mp="f6sqeoinafuw">
                        <div className="flex items-center justify-between text-xs mb-1.5" data-id="element-2358" data-mp="f3wsv1aplt6z">
                          <span className="text-gray-400" data-id="element-2359" data-mp="k39cld6aak5c">Redeemed</span>
                          <span className="font-bold text-white" data-id="element-2360" data-mp="c33feyqh36uc">
                            {redeemed} / {e.totalBoxes}
                          </span>
                        </div>
                        <div className="h-2 rounded-full bg-gray-800 overflow-hidden" data-id="element-2361" data-mp="luhb0vfazmi7">
                          <motion.div className="h-full bg-brand-blue rounded-full" initial={false} animate={{
                    width: `${pct}%`
                  }} transition={{
                    type: 'spring',
                    stiffness: 120,
                    damping: 20
                  }} data-id="element-2362" data-mp="gumlav79l2yq" />
                        </div>
                        <div className="flex items-center justify-between text-[11px] text-gray-500 mt-1.5" data-id="element-2363" data-mp="1ehbsrkkqk7rz">
                          <span data-id="element-2364" data-mp="19jk36x7kcxqy">{remaining} remaining</span>
                          <span data-id="element-2365" data-mp="2rh5gszlp07s5">
                            {next ? `Next: ${next}` : e.date || 'No date'}
                          </span>
                        </div>
                      </div>
                    </button>;
          })}
              </div>}
          </div>}

        {tab === 'verify' && <div className="max-w-md mx-auto space-y-5" data-id="element-2366" data-mp="2nhodabpttkpm">
            <div className="text-center" data-id="element-2367" data-mp="3b6h1tn44cqa7">
              <h1 className="text-xl font-bold" data-id="element-2368" data-mp="3b7s3nhe07nyo">Verify a Redemption</h1>
              <p className="text-sm text-gray-400 mt-1" data-id="element-2369" data-mp="3f6hry50kypx3">
                Scan the customer's QR to unlock and redeem their catch on the
                spot.
              </p>
            </div>

            {showScanner ? <div className="bg-gray-900 border border-gray-800 rounded-2xl p-4" data-id="element-2370" data-mp="36ccsix7ds65y">
                <QrScanner onResult={handleScannedCode} onClose={() => setShowScanner(false)} data-id="element-2371" data-mp="3u2z24sx19yi2" />
              </div> : <Button onClick={() => {
          setVerdict(null);
          setShowScanner(true);
        }} className="w-full flex items-center justify-center gap-2 py-3" data-id="element-2372" data-mp="1yubspz2zm9ek">
                <QrCode className="w-5 h-5" data-id="element-2373" data-mp="1k3uv6qth43my" /> Open Scanner
              </Button>}

            <AnimatePresence mode="wait" data-id="element-2374" data-mp="2hrlcxig4zqwv">
              {verdict && <motion.div key={verdict.title} initial={{
            opacity: 0,
            y: 12,
            scale: 0.98
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} exit={{
            opacity: 0,
            y: -8
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 22
          }} className={`rounded-2xl p-5 border ${verdict.kind === 'valid' ? 'bg-green-500/10 border-green-500/40' : verdict.kind === 'redeemed' ? 'bg-amber-500/10 border-amber-500/40' : verdict.kind === 'drop' ? 'bg-brand-blue/10 border-brand-blue/40' : 'bg-red-500/10 border-red-500/40'}`} data-id="element-2375" data-mp="2hqtr2zccd7js">
                  <div className="flex items-start gap-3" data-id="element-2376" data-mp="3mqibiwgxhxbh">
                    {verdict.kind === 'valid' ? <CheckCircle2 className="w-7 h-7 text-green-400 shrink-0" data-id="element-2377" data-mp="3rpcrlqnih7rw" /> : verdict.kind === 'redeemed' ? <AlertTriangle className="w-7 h-7 text-amber-400 shrink-0" data-id="element-2378" data-mp="3vshqlj8durbs" /> : verdict.kind === 'drop' ? <QrCode className="w-7 h-7 text-brand-blue shrink-0" data-id="element-2379" data-mp="4ij3f909c6fr" /> : <XCircle className="w-7 h-7 text-red-400 shrink-0" data-id="element-2380" data-mp="21fjlrf5gcmvu" />}
                    <div data-id="element-2381" data-mp="26e37beg4vwno">
                      <p className="font-bold text-white" data-id="element-2382" data-mp="1rxjsjph5cejg">{verdict.title}</p>
                      {verdict.detail && <p className="text-sm text-gray-300 mt-0.5" data-id="element-2383" data-mp="1wptphpgoiev8">
                          {verdict.detail}
                        </p>}
                    </div>
                  </div>
                  <button onClick={() => {
              setVerdict(null);
              setShowScanner(true);
            }} className="mt-4 w-full flex items-center justify-center gap-2 text-sm font-bold bg-gray-800 hover:bg-gray-700 text-white rounded-xl py-2.5 transition-colors" data-id="element-2384" data-mp="1mzqpv6f1y2nx">
                    <RotateCcw className="w-4 h-4" data-id="element-2385" data-mp="25ox9cvno6yas" /> Scan another
                  </button>
                </motion.div>}
            </AnimatePresence>

            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-4 text-xs text-gray-500 flex items-start gap-2" data-id="element-2386" data-mp="qaspn8oj9cjd">
              <ScanLine className="w-4 h-4 text-gray-600 mt-0.5 shrink-0" data-id="element-2387" data-mp="mqil701rdalr" />
              <p data-id="element-2388" data-mp="msllqe0jem4q">
                Works with a customer's redemption code QR and recognises drop
                QRs. All verifications sync to Supabase instantly across every
                device.
              </p>
            </div>
          </div>}
      </main>

      {/* Drop detail sheet */}
      <AnimatePresence data-id="element-2389" data-mp="ro0s6kw7tfr4">
        {selected && <DropDetail drop={selected} redeemed={redeemedForDrop(selected)} onClose={() => setSelectedId(null)} data-id="element-2390" data-mp="412ic62lp626" />}
      </AnimatePresence>
    </div>;
}
function DropDetail({
  drop,
  redeemed,
  onClose
}: {
  drop: Event;
  redeemed: number;
  onClose: () => void;
}) {
  const rec = normalizeRecurrence(drop.recurrence);
  const next = nextOccurrence(drop);
  const remaining = Math.max(0, drop.totalBoxes - redeemed);
  const pct = drop.totalBoxes > 0 ? Math.min(100, Math.round(redeemed / drop.totalBoxes * 100)) : 0;
  const verifyLabel = VERIFICATION_METHODS.find(m => m.value === normalizeVerificationMethod(drop.verificationMethod))?.label;
  return <motion.div className="fixed inset-0 z-30 flex items-end sm:items-center justify-center" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} data-id="element-2391" data-mp="9ycqq8tz6yo7">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} data-id="element-2392" data-mp="3j65lcaey7pkr" />
      <motion.div initial={{
      y: '100%',
      opacity: 0.5
    }} animate={{
      y: 0,
      opacity: 1
    }} exit={{
      y: '100%',
      opacity: 0.5
    }} transition={{
      type: 'spring',
      stiffness: 220,
      damping: 28
    }} className="relative w-full sm:max-w-lg bg-gray-900 border border-gray-800 rounded-t-3xl sm:rounded-3xl max-h-[90vh] overflow-y-auto" data-id="element-2393" data-mp="3n80lvcp21yxh">
        <div className="sticky top-0 bg-gray-900/95 backdrop-blur border-b border-gray-800 px-5 py-4 flex items-center justify-between" data-id="element-2394" data-mp="2wx8b1h26au8i">
          <div className="flex items-center gap-3 min-w-0" data-id="element-2395" data-mp="2rynje60zlivo">
            {drop.brandLogo ? <img src={drop.brandLogo} alt="" className="w-10 h-10 rounded-lg object-contain bg-white/5 p-1 shrink-0" data-id="element-2396" data-mp="2n5kkrjhcrj8m" /> : <div className="w-10 h-10 rounded-lg bg-gray-800 flex items-center justify-center shrink-0" data-id="element-2397" data-mp="2e7s86n30mpox">
                <Tag className="w-5 h-5 text-gray-500" data-id="element-2398" data-mp="299gxyb8t6d66" />
              </div>}
            <div className="min-w-0" data-id="element-2399" data-mp="248jhc33g5r6s">
              <h2 className="font-bold text-white truncate" data-id="element-2400" data-mp="1z9gaqe7tjcwp">{drop.title}</h2>
              <p className="text-xs text-blue-400 truncate" data-id="element-2401" data-mp="1zbau3o3fywyo">{drop.brand}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-2 rounded-lg hover:bg-gray-800 transition-colors" aria-label="Close" data-id="element-2402" data-mp="2ek6tjy5fdv5">
            <XCircle className="w-5 h-5" data-id="element-2403" data-mp="5goqru37tm2l" />
          </button>
        </div>

        <div className="p-5 space-y-5" data-id="element-2404" data-mp="agujzep4vgms">
          {/* Counter */}
          <div className="bg-gray-950 border border-gray-800 rounded-2xl p-4" data-id="element-2405" data-mp="3ropmwnwp87v4">
            <div className="flex items-center justify-between mb-2" data-id="element-2406" data-mp="iwxavmx9rwa">
              <span className="text-xs text-gray-400 uppercase tracking-wider font-bold flex items-center gap-1.5" data-id="element-2407" data-mp="l9c4zxdvm54">
                <div className="w-3.5 h-3.5" data-id="element-2408" data-mp="3nnvyzuj9at28" /> Redemption
              </span>
              <span className="font-mono text-[11px] bg-brand-yellow/15 text-brand-yellow px-2 py-0.5 rounded" data-id="element-2409" data-mp="3nq8n6pflxznq">
                {drop.code}
              </span>
            </div>
            <div className="flex items-end justify-between mb-2" data-id="element-2410" data-mp="3dyunohnnozul">
              <p className="text-3xl font-bold text-white" data-id="element-2411" data-mp="2zybfnaj6xtsf">
                {redeemed}
                <span className="text-base text-gray-500 font-medium" data-id="element-2412" data-mp="340ohvoyqb9kg">
                  {' '}
                  / {drop.totalBoxes}
                </span>
              </p>
              <p className="text-sm text-gray-400" data-id="element-2413" data-mp="2ldpeqh45qi2d">{remaining} remaining</p>
            </div>
            <div className="h-2.5 rounded-full bg-gray-800 overflow-hidden" data-id="element-2414" data-mp="2gf4ewisti0eg">
              <motion.div className="h-full bg-brand-blue rounded-full" initial={false} animate={{
              width: `${pct}%`
            }} transition={{
              type: 'spring',
              stiffness: 120,
              damping: 20
            }} data-id="element-2415" data-mp="2uducdzjju23c" />
            </div>
          </div>

          {/* Schedule */}
          <div className="space-y-2.5" data-id="element-2416" data-mp="3d4vdat48c7pj">
            <h3 className="text-xs text-gray-500 uppercase tracking-wider font-bold" data-id="element-2417" data-mp="392i57y20fxpl">
              Schedule
            </h3>
            <Row icon={Calendar} label="Start date" value={drop.date || '—'} data-id="element-2418" data-mp="3n1qa97kuu01p" />
            <Row icon={Clock} label="Time" value={drop.time || '—'} data-id="element-2419" data-mp="3i36mr2qm4g78" />
            <Row icon={Repeat} label="Repeats" value={describeRecurrence(rec)} data-id="element-2420" data-mp="2foz5bk39jufa" />
            {isRecurring(rec) && <Row icon={Calendar} label="Next occurrence" value={next || 'Ended'} data-id="element-2421" data-mp="2jny9h2h67n5o" />}
            <Row icon={MapPin} label="Location" value={drop.address || '—'} data-id="element-2422" data-mp="2tgvylrgo4ad9" />
            <Row icon={ScanLine} label="Verification" value={verifyLabel || '—'} data-id="element-2423" data-mp="2yg068dny201h" />
          </div>

          {drop.description && <p className="text-sm text-gray-300 leading-relaxed" data-id="element-2424" data-mp="37fdh9arpuwob">
              {drop.description}
            </p>}

          {/* Products / brand inspection */}
          <div data-id="element-2425" data-mp="3cclztcgs12q3">
            <h3 className="text-xs text-gray-500 uppercase tracking-wider font-bold mb-2.5" data-id="element-2426" data-mp="1d242b3geiiyo">
              Products in catch ({drop.products?.length || 0})
            </h3>
            {drop.products && drop.products.length > 0 ? <div className="grid grid-cols-1 gap-2" data-id="element-2427" data-mp="1aihgachftgw5">
                {drop.products.map((p, i) => <div key={`${p.name}-${i}`} className="flex items-center gap-3 bg-gray-950 border border-gray-800 rounded-xl p-3" data-id="element-2428" data-mp="15pxvaugf7jhb" data-mpi={i}>
                    {p.logo ? <img src={p.logo} alt="" className="w-9 h-9 rounded-lg object-contain bg-white/5 p-1 shrink-0" data-id="element-2429" data-mp="vqgqef8zt9me" /> : <div className="w-9 h-9 rounded-lg bg-gray-800 flex items-center justify-center shrink-0" data-id="element-2430" data-mp="1jhl2hpbwicev">
                        <Package className="w-4 h-4 text-gray-500" data-id="element-2431" data-mp="1fhaktfeqxxk6" />
                      </div>}
                    <div className="min-w-0" data-id="element-2432" data-mp="83qorf0k3h2b">
                      <p className="font-medium text-white text-sm truncate" data-id="element-2433" data-mp="41dkkv1gjr7n">
                        {p.name}
                      </p>
                      <p className="text-xs text-gray-500 truncate" data-id="element-2434" data-mp="2352kh15lr9p1">
                        {p.brand}
                      </p>
                    </div>
                  </div>)}
              </div> : <p className="text-sm text-gray-500" data-id="element-2435" data-mp="1u7a21o4l3lxf">
                No products listed for this drop.
              </p>}
          </div>
        </div>
      </motion.div>
    </motion.div>;
}
function StatCard({
  icon: Icon,
  label,
  value,
  accent,
  hint
}: {
  icon: ComponentType<{
    className?: string;
  }>;
  label: string;
  value: number;
  accent: string;
  hint?: string;
}) {
  return <div className="bg-gray-900 border border-gray-800 rounded-2xl p-4" data-id="element-2436" data-mp="2d277f379v96w">
      <div className="flex items-center gap-2 mb-2" data-id="element-2437" data-mp="2i23b11c24ylx">
        <Icon className={`w-4 h-4 ${accent}`} data-id="element-2438" data-mp="35t8r9hlwtswr" />
        <span className="text-xs text-gray-400 uppercase tracking-wider font-bold" data-id="element-2439" data-mp="35re7i6mdk6u9">
          {label}
        </span>
      </div>
      <p className={`text-3xl font-bold ${accent}`} data-id="element-2440" data-mp="3alif8s3gsiwe">{value}</p>
      {hint && <p className="text-[11px] text-gray-500 mt-1" data-id="element-2441" data-mp="3jwy1ni297bx6">{hint}</p>}
    </div>;
}
function Row({
  icon: Icon,
  label,
  value
}: {
  icon: ComponentType<{
    className?: string;
  }>;
  label: string;
  value: string;
}) {
  return <div className="flex items-center justify-between gap-3 text-sm" data-id="element-2442" data-mp="3tq51yoi501qe">
      <span className="flex items-center gap-2 text-gray-400" data-id="element-2443" data-mp="3opr23tqovy3g">
        <Icon className="w-4 h-4 text-gray-500 shrink-0" data-id="element-2444" data-mp="30yv6elj44iqx" />
        {label}
      </span>
      <span className="text-white font-medium text-right" data-id="element-2445" data-mp="2wyjmxc2hkoh1">{value}</span>
    </div>;
}